import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';
import { PolicyConsentModalComponent } from '../../shared/components/policy-consent/policy-consent-modal.component';

export interface SLAItem {
  id: string;
  title: string;
  category: string;
  uptimeTarget: number;
  maxLatencyMs: number;
  incidentResponseMins: number;
  penaltyTerms: string;
  status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
}

@Component({
  selector: 'app-sla-management',
  standalone: true,
  imports: [CommonModule, FormsModule, PolicyConsentModalComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- READ-ONLY SLA REVIEW MODAL -->
      @if (showReviewModal()) {
        <div class="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200">
          <div class="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 p-6 sm:p-8 animate-in zoom-in-95 duration-200">
            <app-policy-consent-modal
              title="Official Institution Service Level Agreement (SLA)"
              [institutionName]="authService.institutionName()"
              [isReadOnly]="true"
              [agreedAt]="authService.slaAcceptedAt()"
              [agreedBy]="authService.slaAcceptedBy() || authService.userName()"
              (closed)="showReviewModal.set(false)">
            </app-policy-consent-modal>
          </div>
        </div>
      }

      <!-- Header Section -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 class="text-xl font-extrabold text-slate-900 flex items-center gap-2">
            <svg class="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Service Level Agreements (SLA) Management
          </h1>
          <p class="text-xs text-slate-500 mt-1">
            Define uptime standards, maximum latency thresholds, incident response windows, and penalty rules for e-Gov data sharing APIs.
          </p>
        </div>

        @if (authService.isInternal() || authService.hasRole('SuperAdmin')) {
          <button (click)="openAddModal()" class="px-4 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md shadow-emerald-600/20 flex items-center gap-2 transition">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Create SLA Standard
          </button>
        }
      </div>

      <!-- MASTER BINDING SLA & POLICY AGREEMENT -->
      <div class="p-5 rounded-2xl bg-gradient-to-r from-emerald-50 via-teal-50/70 to-emerald-50 border border-emerald-200/90 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div class="flex items-start gap-3.5">
          <div class="p-3 rounded-xl bg-emerald-600 text-white shadow-md shadow-emerald-600/20 shrink-0">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <div class="flex items-center gap-2 flex-wrap">
              <h2 class="text-base font-bold text-slate-900">
                {{ authService.hasInstitution() ? "Your Institution's Binding SLA & Policy Agreement" : "Master e-Government Service Level Agreement (SLA)" }}
              </h2>
              <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 uppercase tracking-wide flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span>
                Active & Enforced
              </span>
            </div>
            <p class="text-xs text-slate-600 mt-1 leading-relaxed">
              <strong>{{ authService.institutionName() }}</strong> {{ authService.hasInstitution() ? 'has formally signed the' : 'operates under the' }} Master e-Gov Service Level Agreement (SLA Target: 99.90% Uptime, &le;250ms Latency, 15m Incident Escalation).
            </p>
            @if (authService.slaAcceptedAt()) {
              <div class="text-[11px] text-slate-500 mt-1.5 flex items-center gap-3">
                <span><strong>Executed:</strong> {{ authService.slaAcceptedAt() }}</span>
                <span><strong>Signatory:</strong> {{ authService.slaAcceptedBy() || authService.userName() }}</span>
              </div>
            }
          </div>
        </div>

        <button
          type="button"
          (click)="showReviewModal.set(true)"
          class="px-4 py-2.5 rounded-xl text-xs font-bold bg-white hover:bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-xs flex items-center justify-center gap-2 transition shrink-0 cursor-pointer"
        >
          <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Review Terms (Read-Only)
        </button>
      </div>

      <!-- Additional Custom SLA Standards (if any) -->
      @if (slaList().length > 0) {
        <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div *ngFor="let item of slaList()" class="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 shadow-sm hover:border-emerald-300 hover:shadow-md transition-all">
            <div class="flex items-center justify-between">
              <span class="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200">
                {{ item.category }}
              </span>
              <span [ngClass]="{
                'bg-emerald-50 text-emerald-700 border-emerald-200': item.status === 'ACTIVE',
                'bg-amber-50 text-amber-700 border-amber-200': item.status === 'DRAFT'
              }" class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border">
                {{ item.status }}
              </span>
            </div>

            <div>
              <h3 class="text-base font-bold text-slate-900">{{ item.title }}</h3>
              <p class="text-xs text-slate-600 mt-1 line-clamp-2 leading-relaxed">{{ item.penaltyTerms }}</p>
            </div>

            <div class="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-center">
              <div class="p-2 rounded-xl bg-slate-50 border border-slate-200">
                <div class="text-[10px] text-slate-500 font-bold uppercase">Uptime</div>
                <div class="text-sm font-extrabold text-emerald-700 mt-0.5">{{ item.uptimeTarget }}%</div>
              </div>
              <div class="p-2 rounded-xl bg-slate-50 border border-slate-200">
                <div class="text-[10px] text-slate-500 font-bold uppercase">Max Latency</div>
                <div class="text-sm font-extrabold text-sky-700 mt-0.5">{{ item.maxLatencyMs }}ms</div>
              </div>
              <div class="p-2 rounded-xl bg-slate-50 border border-slate-200">
                <div class="text-[10px] text-slate-500 font-bold uppercase">Max Window</div>
                <div class="text-sm font-extrabold text-purple-700 mt-0.5">{{ item.incidentResponseMins }}m</div>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Add SLA Modal -->
      <div *ngIf="showModal()" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
        <div class="bg-white border border-slate-200 rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl">
          <div class="flex items-center justify-between border-b border-slate-200 pb-3">
            <h3 class="text-base font-bold text-slate-900">Create New SLA Standard</h3>
            <button (click)="showModal.set(false)" class="text-slate-400 hover:text-slate-900">✕</button>
          </div>

          <div class="space-y-3 text-xs">
            <div>
              <label class="block text-slate-700 font-bold mb-1">SLA Standard Title</label>
              <input type="text" [(ngModel)]="newSla.title" placeholder="e.g. Critical Realtime Identity SLA" class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none" />
            </div>

            <div>
              <label class="block text-slate-700 font-bold mb-1">Category</label>
              <select [(ngModel)]="newSla.category" class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none">
                <option value="CRITICAL_INFRASTRUCTURE">Critical Infrastructure</option>
                <option value="STANDARD_DATA">Standard Data Exchange</option>
                <option value="BATCH_ANALYTICS">Batch Analytics</option>
              </select>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-slate-700 font-bold mb-1">Uptime Target (%)</label>
                <input type="number" [(ngModel)]="newSla.uptimeTarget" class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none" />
              </div>
              <div>
                <label class="block text-slate-700 font-bold mb-1">Max Latency (ms)</label>
                <input type="number" [(ngModel)]="newSla.maxLatencyMs" class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none" />
              </div>
            </div>

            <div>
              <label class="block text-slate-700 font-bold mb-1">Penalty & Non-Compliance Terms</label>
              <textarea [(ngModel)]="newSla.penaltyTerms" rows="3" placeholder="Specify automatic throttling or compliance flags upon SLA breach..." class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none"></textarea>
            </div>
          </div>

          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button (click)="showModal.set(false)" class="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300">Cancel</button>
            <button (click)="saveSla()" class="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 shadow-sm">Save SLA</button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class SlaManagementComponent {
  authService = inject(AuthService);
  showModal = signal(false);
  showReviewModal = signal(false);

  slaList = signal<SLAItem[]>([]);

  newSla: Partial<SLAItem> = {
    title: '',
    category: 'CRITICAL_INFRASTRUCTURE',
    uptimeTarget: 99.9,
    maxLatencyMs: 250,
    incidentResponseMins: 30,
    penaltyTerms: '',
    status: 'ACTIVE'
  };

  openAddModal() {
    this.showModal.set(true);
  }

  saveSla() {
    if (!this.newSla.title) return;
    this.slaList.update(list => [
      ...list,
      {
        id: `SLA-${list.length + 1}`,
        title: this.newSla.title!,
        category: this.newSla.category || 'STANDARD_DATA',
        uptimeTarget: this.newSla.uptimeTarget || 99.9,
        maxLatencyMs: this.newSla.maxLatencyMs || 300,
        incidentResponseMins: this.newSla.incidentResponseMins || 30,
        penaltyTerms: this.newSla.penaltyTerms || 'Default governance warning.',
        status: 'ACTIVE'
      }
    ]);
    this.showModal.set(false);
  }
}
